import React, { useState, useEffect } from 'react';
import Auth from './pages/Auth';
import AdminUsers from './pages/AdminUsers';
import AdminClusters from './pages/AdminClusters';
import AdminCantieri from './pages/AdminCantieri';
import AdminConfig from './pages/AdminConfig';
import UserDashboard from './pages/UserDashboard';
import CantiereBrowser from './pages/CantiereBrowser';
import ObjectDashboard from './pages/ObjectDashboard';
import ManageIngestion from './pages/ManageIngestion';
import Documentation from './pages/Documentation';

import { Cantiere } from './types';
import { NotificationProvider, useNotification } from './components/NotificationContext';
import ActiveUsersBadge from './components/ActiveUsersBadge';

const NotificationArea: React.FC = () => {
  const { message, type, clearNotification } = useNotification();

  if (!message) return null;

  const getStyle = () => {
    switch (type) {
      case 'success': return { background: '#4caf50', color: 'white' };
      case 'error': return { background: '#f44336', color: 'white' };
      default: return { background: '#2196f3', color: 'white' };
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      padding: '12px 24px',
      zIndex: 3000,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      ...getStyle()
    }}>
      <span>{message}</span>
      <button
        onClick={clearNotification}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '1.2rem',
          marginLeft: '16px'
        }}
      >
        <i className="fas fa-xmark"></i>
      </button>
    </div >
  );
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string>(() => localStorage.getItem('user_role') || 'operator');
  const isClient = userRole === 'client';
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedCantiereId, setSelectedCantiereId] = useState<number | string | null>(null);
  const [selectedCantiere, setSelectedCantiere] = useState<Cantiere | null>(null);
  const [selectedObjectName, setSelectedObjectName] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const storedIsAdmin = localStorage.getItem('is_admin') === 'true';
    const storedRole = localStorage.getItem('user_role') || (storedIsAdmin ? 'admin' : 'operator');
    if (token) {
      setIsLoggedIn(true);
      setIsAdmin(storedIsAdmin);
      setUserRole(storedRole);
      setCurrentView('dashboard');
    }
  }, []);

  const handleLoginSuccess = (adminStatus: boolean, role?: string) => {
    setIsLoggedIn(true);
    setIsAdmin(adminStatus);
    const effectiveRole = role || (adminStatus ? 'admin' : 'operator');
    setUserRole(effectiveRole);
    localStorage.setItem('is_admin', adminStatus.toString());
    localStorage.setItem('user_role', effectiveRole);
    setCurrentView('dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('is_admin');
    localStorage.removeItem('user_role');
    setIsLoggedIn(false);
    setIsAdmin(false);
    setUserRole('operator');
    setCurrentView('dashboard');
    setSelectedCantiereId(null);
    setSelectedObjectName(null);
  };

  const handleSelectCantiere = (cantiere: Cantiere) => {
    setSelectedCantiereId(cantiere.id);
    setSelectedCantiere(cantiere);
    setCurrentView('cantiereBrowser');
  };

  const handleSelectDigitalObject = (cantiereId: number | string, objectName: string) => {
    setSelectedCantiereId(cantiereId);
    setSelectedObjectName(objectName);
    setCurrentView('objectDashboard');
  };

  const handleBackToCantieri = () => {
    setCurrentView('dashboard');
    setSelectedCantiereId(null);
    setSelectedCantiere(null);
    setSelectedObjectName(null);
  };

  const handleBackToCantiereBrowser = () => {
    setCurrentView('cantiereBrowser');
    setSelectedObjectName(null);
  };

  const isAdminView = (view: string) => {
    return ['adminUsers', 'adminClusters', 'adminCantieri', 'manageIngestion', 'adminConfig'].includes(view);
  };

  const renderContent = () => {
    if (!isLoggedIn) {
      return <Auth onLoginSuccess={handleLoginSuccess} />;
    }

    switch (currentView) {
      case 'adminUsers':
      case 'adminClusters':
      case 'adminCantieri':
      case 'manageIngestion':
      case 'adminConfig':
        return (
          <div>
            <nav>
              <button className="btn btn-text" onClick={() => setCurrentView('adminClusters')}>Manage Clusters</button>
              <button className="btn btn-text" onClick={() => setCurrentView('adminCantieri')}>Manage Cantieri</button>
              <button className="btn btn-text" onClick={() => setCurrentView('manageIngestion')}>Manage Ingestion</button>
              <button className="btn btn-text" onClick={() => setCurrentView('adminConfig')}>Config</button>
              <button className="btn btn-text" onClick={() => setCurrentView('adminUsers')}>Manage Users</button>
            </nav>
            {currentView === 'adminUsers' && <AdminUsers />}
            {currentView === 'adminClusters' && <AdminClusters />}
            {currentView === 'adminCantieri' && <AdminCantieri onSelectCantiere={handleSelectCantiere} />}
            {currentView === 'manageIngestion' && <ManageIngestion />}
            {currentView === 'adminConfig' && <AdminConfig />}
          </div>
        );
      case 'cantiereBrowser':
        return selectedCantiere && (
          <CantiereBrowser
            cantiere={selectedCantiere}
            onSelectDigitalObject={handleSelectDigitalObject}
            onBack={handleBackToCantieri}
            isClient={isClient}
          />
        );
      case 'objectDashboard':
        return selectedCantiereId && selectedObjectName && (
          <ObjectDashboard
            cantiereId={selectedCantiere!.id}
            dpacCode={selectedCantiere!.dpac_code || ''}
            cantiereName={selectedCantiere!.name || ''}
            objectName={selectedObjectName}
            onBack={handleBackToCantiereBrowser}
            canEdit={!isClient && selectedCantiere?.can_edit}
            isClient={isClient}
            isAdmin={isAdmin}
          />
        );
      case 'documentation':
        return <Documentation />;
      case 'dashboard':
      default:
        return <UserDashboard onSelectCantiere={handleSelectCantiere} isAdmin={isAdmin} isClient={isClient} />;
    }
  };

  return (
    <div className="App">
      <NotificationArea />
      {isLoggedIn && (
        <header className="app-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h1 style={{ margin: 0 }}>SmartPack</h1>
            <ActiveUsersBadge
              userRole={userRole}
              isAdmin={isAdmin}
              currentCantiereId={selectedCantiereId}
              currentCantiereName={selectedCantiere?.name}
              currentView={currentView}
              selectedObjectName={selectedObjectName}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {(currentView === 'documentation' || (isAdmin && isAdminView(currentView))) && (
              <button
                className="btn btn-text"
                style={{ color: 'white' }}
                onClick={() => setCurrentView('dashboard')}
              >
                DASHBOARD
              </button>
            )}
            {isAdmin && !isAdminView(currentView) && (
              <button
                className="btn btn-text"
                style={{ color: 'white' }}
                onClick={() => setCurrentView('adminCantieri')}
              >
                AMMINISTRAZIONE
              </button>
            )}
            <button
              className="btn btn-text"
              style={{ color: 'white', fontWeight: currentView === 'documentation' ? 'bold' : 'normal' }}
              onClick={() => setCurrentView('documentation')}
            >
              DOCUMENTAZIONE
            </button>
            <button className="btn btn-text" style={{ color: 'white' }} onClick={handleLogout}>Logout</button>
          </div>
        </header>
      )}
      <main className={isLoggedIn ? "container" : ""}>
        {renderContent()}
      </main>
    </div>
  );
}

const WrappedApp = () => (
  <NotificationProvider>
    <App />
  </NotificationProvider>
);

export default WrappedApp;